<template>
  <Button variant="outline" @click="openDialog">
    <UploadIcon class="tw:w-3 tw:h-3" /> {{ $t('Export to Excel format') }}
  </Button>

  <Dialog :open="open" @update:open="open = $event">
    <DialogScrollContent>
      <DialogHeader>
        <DialogTitle>{{ $t('Choose columns to export') }}</DialogTitle>
      </DialogHeader>

      <div v-if="loading" class="tw:py-6 tw:text-center tw:text-sm tw:text-zinc-500">
        {{ $t('Loading...') }}
      </div>
      <div v-else-if="error" class="tw:py-6 tw:text-center tw:text-sm tw:text-red-600">
        {{ $t('An error occurred') }}
      </div>

      <template v-else>
        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <span class="tw:text-sm tw:text-zinc-500">{{ $t('Preset') }}</span>
          <button
            v-for="preset in presets"
            :key="preset.name"
            type="button"
            class="tw:rounded-full tw:border tw:px-3 tw:py-1 tw:text-xs tw:cursor-pointer"
            :class="preset.name === activePreset
              ? 'tw:border-blue-600 tw:bg-blue-50 tw:text-blue-700'
              : 'tw:border-zinc-200 tw:text-zinc-600 tw:hover:bg-zinc-50'"
            @click="applyPreset(preset)"
          >
            {{ $t(presetLabels[preset.name] ?? preset.name) }}
          </button>
        </div>

        <div class="tw:max-h-[55vh] tw:overflow-y-auto tw:pr-1">
          <label class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:text-sm tw:font-medium tw:text-zinc-600 tw:cursor-pointer tw:select-none">
            <input
              type="checkbox"
              class="tw:accent-blue-600"
              :checked="allSelected"
              :indeterminate="selected.length > 0 && !allSelected"
              @change="toggleAll"
            />
            {{ $t('Select all') }}
          </label>

          <Collapsible
            v-for="group in groupedColumns"
            :key="group.name"
            v-slot="{ open }"
            class="tw:block tw:border-t tw:border-zinc-100 tw:py-1"
          >
            <div class="tw:flex tw:items-center tw:gap-2 tw:py-1.5">
              <input
                :id="`export-column-group-${group.name}`"
                type="checkbox"
                class="tw:accent-blue-600"
                :checked="isGroupSelected(group)"
                :indeterminate="isGroupPartiallySelected(group)"
                @change="toggleGroup(group)"
              />
              <label
                :for="`export-column-group-${group.name}`"
                class="tw:flex-1 tw:text-sm tw:font-medium tw:text-zinc-700 tw:cursor-pointer tw:select-none"
              >
                {{ $t(group.name) }}
              </label>
              <span v-if="!isGroupSelected(group)" class="tw:text-xs tw:text-zinc-400 tw:tabular-nums">
                {{ selectedInGroup(group) }}/{{ group.columns.length }}
              </span>
              <CollapsibleTrigger
                class="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-zinc-500 tw:hover:text-zinc-800 tw:cursor-pointer"
              >
                {{ open ? $t('Collapse') : $t('Customize') }}
                <ChevronUpIcon v-if="open" class="tw:w-3 tw:h-3" />
                <ChevronDownIcon v-else class="tw:w-3 tw:h-3" />
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent class="tw:flex tw:flex-col tw:pb-1">
              <label
                v-for="column in group.columns"
                :key="column.key"
                class="tw:flex tw:items-center tw:gap-2 tw:py-1 tw:text-sm tw:text-zinc-600 tw:cursor-pointer tw:select-none tw:hover:text-zinc-900"
              >
                <input type="checkbox" class="tw:accent-blue-600" :value="column.key" v-model="selected" />
                <span class="tw:truncate">{{ $t(column.label) }}</span>
              </label>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </template>

      <DialogFooter class="tw:items-center tw:justify-between tw:gap-2">
        <span class="tw:text-sm tw:text-zinc-500">
          {{ $t('{count} columns selected', { count: selected.length }) }}
        </span>
        <Button v-if="selected.length" as-child>
          <a :href="exportUrl" target="_blank" @click="open = false">{{ $t('Export') }}</a>
        </Button>
        <Button v-else disabled>{{ $t('Export') }}</Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import axios from "axios";
import { Button } from "@/shadcn/components/ui/button";
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ChevronDownIcon, ChevronUpIcon, UploadIcon } from "@lucide/vue";
import Collapsible from "@/components/ui/Collapsible.vue";
import { CollapsibleContent, CollapsibleTrigger } from "@/shadcn/components/ui/collapsible";

interface ExportColumn {
  key: string;
  label: string;
  group: string;
}

interface ColumnGroup {
  name: string;
  columns: ExportColumn[];
}

interface ExportPreset {
  name: string;
  columns: string[];
}

/** Preset names come from the API; their wording belongs here. */
const presetLabels: Record<string, string> = {
  basic: 'Basic',
  advanced: 'Advanced',
  full: 'Full',
};

const props = defineProps<{
  /** The picked column keys; the parent owns them and builds the export URL from them. */
  modelValue: string[];
  exportUrl: string;
  columnsUrl: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string[]];
}>();

const selected = computed({
  get: () => props.modelValue,
  set: (value: string[]) => emit('update:modelValue', value),
});

const open = ref(false);
const loading = ref(false);
const error = ref(false);
const columns = ref<ExportColumn[]>([]);
const presets = ref<ExportPreset[]>([]);

const groupedColumns = computed<ColumnGroup[]>(() => {
  const groups: ColumnGroup[] = [];

  for (const column of columns.value) {
    let group = groups.find((candidate) => candidate.name === column.group);

    if (!group) {
      group = { name: column.group, columns: [] };
      groups.push(group);
    }

    group.columns.push(column);
  }

  return groups;
});

const allSelected = computed(
  () => columns.value.length > 0 && selected.value.length === columns.value.length
);

/** The preset the current selection matches exactly, if any. */
const activePreset = computed<string | null>(() => {
  const preset = presets.value.find((candidate) =>
    candidate.columns.length === selected.value.length
    && candidate.columns.every((key) => selected.value.includes(key))
  );

  return preset?.name ?? null;
});

async function openDialog(): Promise<void> {
  open.value = true;

  if (columns.value.length > 0) {
    return;
  }

  loading.value = true;
  error.value = false;

  try {
    const { data } = await axios.get(props.columnsUrl);
    columns.value = data.data as ExportColumn[];
    presets.value = (data.meta?.presets ?? []) as ExportPreset[];
    // The API sends its presets from the narrowest to the widest; the dialog opens on the first.
    selected.value = presets.value[0]
      ? [...presets.value[0].columns]
      : columns.value.map((column) => column.key);
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
}

function applyPreset(preset: ExportPreset): void {
  selected.value = [...preset.columns];
}

function toggleAll(event: Event): void {
  selected.value = (event.target as HTMLInputElement).checked
    ? columns.value.map((column) => column.key)
    : [];
}

function selectedInGroup(group: ColumnGroup): number {
  return group.columns.filter((column) => selected.value.includes(column.key)).length;
}

function isGroupSelected(group: ColumnGroup): boolean {
  return group.columns.every((column) => selected.value.includes(column.key));
}

function isGroupPartiallySelected(group: ColumnGroup): boolean {
  const count = selectedInGroup(group);

  return count > 0 && count < group.columns.length;
}

function toggleGroup(group: ColumnGroup): void {
  const keys = group.columns.map((column) => column.key);

  selected.value = isGroupSelected(group)
    ? selected.value.filter((key) => !keys.includes(key))
    : [...new Set([...selected.value, ...keys])];
}
</script>
